import {
  type VendorCatalogPrice,
  type VendorCatalogProductDetail,
  type VendorCatalogProductDetailResult,
} from '@choco-admin/types';

import {
  type SuppliersPageVendorCatalogUnitDetailFragment,
  type SuppliersPageVendorCatalogUnitDetailResponseFragment,
} from '../../../../../../../generated';

const DEFAULT_ERROR_MESSAGE = 'Failed to load product details.';

const mapPrice = (
  money:
    | { amount: number; currency: string; decimalPlaces: number }
    | null
    | undefined
): VendorCatalogPrice | null =>
  money
    ? {
        amount: money.amount,
        currency: money.currency,
        decimalPlaces: money.decimalPlaces,
      }
    : null;

const mapUnit = (
  unit: SuppliersPageVendorCatalogUnitDetailFragment
): VendorCatalogProductDetail => {
  const item = unit.vendorItem?.node;

  return {
    id: unit.id,
    // "Product" is the parent VendorItem name (overridable per unit); `unit.name`
    // is the packaging unit shown separately as `unitName`.
    name: unit.itemNameOverride ?? item?.name ?? unit.name,
    unitName: unit.name,
    description: unit.itemDescriptionOverride ?? item?.description ?? null,
    productCode: unit.productCode ?? null,
    matchingCode: unit.code ?? null,
    legacyId: unit.legacyId ?? null,
    packagingDescription: unit.packagingDescription ?? null,
    brand: item?.brand ?? null,
    originCountryCode: item?.originCountryCode ?? null,
    categories: (item?.categories ?? []).map((category) => ({
      id: category.id,
      name: category.name,
    })),
    stockStatus: unit.stockStatus,
    isEnabled: unit.isOrdering,
    isHiddenByDefault: unit.isHiddenByDefault ?? false,
    imageUrls: (unit.images ?? [])
      .map((image) => image.asset?.url?.url)
      .filter((url): url is string => Boolean(url)),
    catalogPrice: mapPrice(unit.price?.displayCatalogPrice),
    basePrice: mapPrice(unit.price?.displayBasePrice),
    displayPrice: mapPrice(unit.price?.displayPrice),
    baseUnitName: unit.baseUnit?.name ?? null,
    factor: unit.factor
      ? {
          numerator: unit.factor.numerator,
          denominator: unit.factor.denominator,
        }
      : null,
    leadTimeDays: unit.orderingProperties?.leadTimeDays ?? null,
    cutOffTime: unit.orderingProperties?.cutOffTime ?? null,
  };
};

/**
 * Maps a `VendorItemUnitsResponse` union into a single-product detail result.
 * Returns `null` when there is no response yet, or when the success result
 * contained no unit (product not found). Failure members become
 * `{ kind: 'error' }`.
 */
export const mapVendorItemUnitDetailResponse = (
  response:
    | SuppliersPageVendorCatalogUnitDetailResponseFragment
    | null
    | undefined
): VendorCatalogProductDetailResult | null => {
  if (!response) {
    return null;
  }

  switch (response.__typename) {
    case 'VendorItemUnitsResult': {
      const unit = response.itemUnits?.[0];
      return unit ? { kind: 'success', detail: mapUnit(unit) } : null;
    }
    case 'InputValidationFailure':
    case 'AuthorizationFailure':
      return {
        kind: 'error',
        message: response.message ?? DEFAULT_ERROR_MESSAGE,
      };
    default:
      return { kind: 'error', message: DEFAULT_ERROR_MESSAGE };
  }
};
